import { Mail, Phone, MapPin, Tag, Globe, CalendarClock, MessageSquare, Check, UserRound, Flag, Building2, Megaphone } from 'lucide-react';
import { fmtDateTime } from './AdminUI';

const STAGE_STYLES = {
  Lead: 'bg-sky-50 text-sky-700',
  Prospect: 'bg-amber-50 text-amber-700',
  Deal: 'bg-emerald-50 text-emerald-700',
  Lost: 'bg-slate-100 text-slate-500',
};

export const Chip = ({ Icon, children, className = 'bg-slate-100 text-slate-700', testId }) => (
  <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold ${className}`} data-testid={testId}>
    {Icon && <Icon className="h-3 w-3" />}{children}
  </span>
);

export const StageChip = ({ stage }) => <Chip className={STAGE_STYLES[stage]}>{stage}</Chip>;

export const LeadCard = ({ lead, onOpen, onMarkReviewed }) => {
  const isNew = !lead.reviewed;
  const due = lead.follow_up_at && new Date(lead.follow_up_at) <= new Date() && lead.stage !== 'Lost';
  const location = [lead.city, lead.zip].filter(Boolean).join(' ');
  return (
    <article
      onClick={() => onOpen(lead)}
      className={`group cursor-pointer rounded-2xl border bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${isNew ? 'border-[#F58220]/50 ring-1 ring-[#F58220]/20' : 'border-slate-200'}`}
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {isNew && <span className="inline-flex items-center gap-1 rounded-full bg-[#F58220] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white" data-testid="lead-new-badge">New</span>}
            <h3 className={`truncate font-sans text-lg normal-case ${isNew ? 'font-bold text-slate-900' : 'font-semibold text-slate-700'}`} data-testid="lead-name">{lead.name}</h3>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
            <a href={`mailto:${lead.email}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 text-[#B85A0A] hover:underline"><Mail className="h-3.5 w-3.5" />{lead.email}</a>
            <a href={`tel:${lead.phone}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1.5 text-slate-600 hover:underline"><Phone className="h-3.5 w-3.5" />{lead.phone}</a>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500" data-testid="lead-date">{fmtDateTime(lead.created_at)}</span>
          {isNew && (
            <button onClick={(e) => { e.stopPropagation(); onMarkReviewed(lead); }} title="Mark reviewed" aria-label="Mark reviewed"
              className="grid h-8 w-8 place-items-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-600" data-testid={`lead-mark-reviewed-${lead.id}`}>
              <Check className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <Chip Icon={Tag} className="bg-orange-50 text-[#B85A0A]">{lead.service}</Chip>
        <Chip Icon={MapPin} className="bg-sky-50 text-sky-800">{location || <span className="italic">City unavailable</span>}</Chip>
        <Chip Icon={Globe}>{lead.channel || 'Source unavailable'}</Chip>
        {lead.campaign && <Chip Icon={Megaphone} className="bg-violet-50 text-violet-700">{lead.campaign}</Chip>}
        {lead.property_type !== 'Unknown' && <Chip Icon={Building2}>{lead.property_type}</Chip>}
        <StageChip stage={lead.stage} />
        {lead.owner_name && <Chip Icon={UserRound}>{lead.owner_name}</Chip>}
        {lead.follow_up_at && <Chip Icon={CalendarClock} className={due ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-700'} testId="lead-followup-chip">{due ? 'Follow-up due' : 'Follow-up'} {new Date(lead.follow_up_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</Chip>}
        {lead.sms_consent && <Chip Icon={MessageSquare} className="bg-emerald-50 text-emerald-700">SMS consent</Chip>}
        {lead.flag && <Chip Icon={Flag} className="bg-rose-100 text-rose-700" testId="lead-flag-chip">{lead.flag === 'spam' ? 'Spam' : 'Test'}</Chip>}
        {lead.notes?.length > 0 && <Chip>{lead.notes.length} note{lead.notes.length > 1 ? 's' : ''}</Chip>}
      </div>
      {lead.message && <p className="mt-3 line-clamp-2 rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600">{lead.message}</p>}
    </article>
  );
};
